/**
 * Embedded task-list presentation: rolls the evidence flags into one pill, flags mandatory tasks, and
 * marks a Visit's changed rows.
 *
 * One module where there used to be a live one and a template one. The two resolvers a definition
 * cannot answer — `startsOn` (no start date) and `visitChange` (no attendance) — return null on a
 * template row of their own accord, because a template task carries neither field at all. The manifest
 * board still lists which properties each parent OFFERS; these only say how a present value renders.
 */
import { TASK_EVIDENCE_REQUIREMENT_FIELDS } from '@jms/model';
import { PlannedTaskLine } from '@jms/model/entities/planned.work/planned.task/planned.task';
import type { EntityBoardListPropertyPresentation, EntityBoardListPropertyResolver, EntityBoardListTitleAdornmentResolver } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { DateTime } from 'luxon';

const evidenceLabels: Record<(typeof TASK_EVIDENCE_REQUIREMENT_FIELDS)[number], string> = {
  signatureEnabled: 'Signature',
  commentEnabled: 'Comment',
  beforePhotoEnabled: 'Before photo',
  duringPhotoEnabled: 'During photo',
  afterPhotoEnabled: 'After photo',
  formEnabled: 'Form',
  fileEnabled: 'File',
};

export const resolvePlannedTaskEvidence: EntityBoardListPropertyResolver = ({ item }): EntityBoardListPropertyPresentation | null => {

  const task = item as PlannedTaskLine;
  const requirements = TASK_EVIDENCE_REQUIREMENT_FIELDS.filter(field => task[field] === true).map(field => evidenceLabels[field]);
  if (!requirements.length) return null;
  return { label: `${requirements.length} evidence`, tooltip: requirements.join(', '), iconLeft: 'fa-regular fa-clipboard-list', tone: 'muted', dashed: true };
};

export const resolvePlannedTaskTitleAdornment: EntityBoardListTitleAdornmentResolver = item => {

  const task = item as PlannedTaskLine;
  if (task.mandatory !== true && task.mandatoryForClaiming !== true) return null;
  return { icon: 'fa-solid fa-asterisk', label: 'Mandatory', tone: 'error' };
};

/** Live parents only: a definition has no start date, so a template row never carries the field. */
export const resolvePlannedTaskStartsOn: EntityBoardListPropertyResolver = ({ item }): EntityBoardListPropertyPresentation | null => {

  const startsOnValue = (item as { startsOn?: Date | string }).startsOn;
  if (!startsOnValue) return null;
  const startsOn = startsOnValue instanceof Date ? DateTime.fromJSDate(startsOnValue, { zone: 'utc' }) : DateTime.fromISO(String(startsOnValue), { zone: 'utc' });
  if (!startsOn.isValid || startsOn.startOf('day') <= DateTime.utc().startOf('day')) return null;
  return { label: `Starts ${startsOn.toFormat('d LLL')}`, tooltip: `This task becomes due from ${startsOn.toFormat('d LLLL yyyy')}`, iconLeft: 'fa-regular fa-calendar', tone: 'muted' };
};

/** Visit state uses the standard list badge slot and leaves inherited rows unmarked. */
export const resolvePlannedTaskVisitChange: EntityBoardListPropertyResolver = ({ item }): EntityBoardListPropertyPresentation | null => {

  const task = item as PlannedTaskLine;
  return task.visitChange ? { label: task.visitChange, tone: 'neutral' } : null;
};
